/**
 * GenericInputData — Institutional-Grade Data Capture Layer
 * Oko-Oba Biogas Project Input Page
 *
 * Upgrade from P1-A to full institutional data capture:
 * - Per-field metadata: source_tier (T1–T4), source_type, source_reference, comment
 * - Data role tagging (climate, circularity, finance, sdg, carbon_pathway, reporting, mrv)
 * - Dynamic feedstock streams (repeatable, % validation)
 * - Real-time validation engine (blocking + warnings)
 * - Live confidence score + completeness in sticky right panel
 * - Insight triggers (smart contextual feedback)
 * - PoA structured config
 * - Evidence linking per field
 * - 2-column layout with sticky intelligence panel
 *
 * ZERO REGRESSION: calculation engine, API contracts, reports — all unchanged.
 * Extended payload includes metadata suffixes (_source_type, _source_ref, _comment)
 * which are ignored by the calculation engine.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, Info, ChevronDown, ChevronUp,
  Upload, AlertTriangle, CheckCircle2, FileText, AlertCircle, FlaskConical,
  PlusCircle, XCircle, Lightbulb, Link2, ChevronRight, BarChart2,
  Shield, Zap, DollarSign, GitBranch, Lock,
} from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useProjectWorkspace, invalidateProjectWorkspaceCache } from "@/hooks/useProjectWorkspace";
import { PageHeader } from "@/components/ds";
import {
  CredibilityStatusBanner, MethodologyPanel, ConfidenceBreakdownPanel,
  FinancialAssumptionsPanel, CarbonReadinessPanel, DisclosureNotice,
} from "@/components/CredibilityLayer";
import VersionHistoryDrawer from "@/components/workspace/VersionHistoryDrawer";

// ── Constants ──────────────────────────────────────────────────────────────────

const TIER_OPTIONS = [
  { value: "T1", label: "T1", full: "Measured",    desc: "Calibrated meter / weigh-bridge / lab analysis",  color: "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200" },
  { value: "T2", label: "T2", full: "Estimated",   desc: "Calculated from records / engineering estimate",   color: "bg-amber-100  text-amber-800  border-amber-200  hover:bg-amber-200" },
  { value: "T3", label: "T3", full: "Default",     desc: "IPCC default / assumed value",                    color: "bg-slate-100  text-slate-600  border-slate-200  hover:bg-slate-200" },
  { value: "T4", label: "T4", full: "Proxy",       desc: "Regional proxy / comparable project data",        color: "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200" },
];
const TIER_ACTIVE = {
  T1: "bg-emerald-500 text-white border-emerald-500 ring-2 ring-emerald-300",
  T2: "bg-amber-400  text-white border-amber-400  ring-2 ring-amber-200",
  T3: "bg-slate-400  text-white border-slate-400  ring-2 ring-slate-200",
  T4: "bg-purple-500 text-white border-purple-500 ring-2 ring-purple-300",
};
const TIER_WEIGHT = { T1: 1.0, T2: 0.7, T3: 0.4, T4: 0.3 };

const SOURCE_TYPE_OPTIONS = [
  { value: "measured",              label: "Measured (T1) — direct instrument measurement" },
  { value: "site_record",           label: "Site Record — operational logs / weighbridge" },
  { value: "engineering_estimate",  label: "Engineering Estimate — design spec / BMP test" },
  { value: "default",               label: "Default — IPCC / regional default value" },
  { value: "proxy",                 label: "Proxy — comparable project data" },
  { value: "regulatory_data",       label: "Regulatory Data — government / utility records" },
];

const ROLE_ICONS = {
  climate:        { icon: "🌍", color: "text-emerald-600", label: "Climate" },
  circularity:    { icon: "♻", color: "text-teal-600",    label: "Circularity" },
  finance:        { icon: "$",  color: "text-blue-600",    label: "Finance" },
  sdg:            { icon: "⭐", color: "text-amber-600",  label: "SDG" },
  carbon_pathway: { icon: "C",  color: "text-purple-600", label: "Carbon" },
  reporting:      { icon: "📋", color: "text-slate-600",  label: "Reporting" },
  mrv:            { icon: "✓",  color: "text-orange-600", label: "MRV" },
};

// ── Insight Trigger Engine ─────────────────────────────────────────────────────
function computeInsights(form, fieldMeta, fields) {
  const insights = [];

  if (form.electricity_displacement_type === "diesel_generator") {
    insights.push({ level: "info", field: "electricity_displacement_type",
      message: "Diesel displacement significantly increases carbon credit value (+78% vs grid). Confirm this assumption with site energy audit.",
      action: "Check energy source and confirm displacement basis" });
  }
  if (!form.offtake_type || form.offtake_type === "") {
    insights.push({ level: "warning", field: "offtake_type",
      message: "No offtake agreement — revenue stream is unconfirmed. DFI lenders require minimum signed offtake or captive use declaration.",
      action: "Secure PPA or captive use declaration before financing engagement" });
  }
  const feedstock_t = parseFloat(form.feedstock_tonnes_yr || 0);
  if (feedstock_t > 0 && feedstock_t < 1000) {
    insights.push({ level: "info", field: "feedstock_tonnes_yr",
      message: "Low feedstock volume (<1,000 t/yr) may yield insufficient carbon credits for VCM market engagement (typically min. 100 tCO₂e/yr).",
      action: "Verify scale is sufficient for target carbon pathway" });
  }
  const t3Count = Object.keys(fieldMeta).filter(k => fieldMeta[k]?.source_tier === "T3").length;
  if (t3Count > 4) {
    insights.push({ level: "warning", field: "_global",
      message: `${t3Count} fields using IPCC defaults (T3) — confidence impact. Upgrade key parameters to T1/T2 with site measurements.`,
      action: "Prioritise: biogas yield, capture efficiency, feedstock weight" });
  }
  const capture = parseFloat(form.capture_efficiency_pct || 0);
  if (capture > 0 && capture < 60) {
    insights.push({ level: "warning", field: "capture_efficiency_pct",
      message: "Capture efficiency <60% is below industry standard. Well-maintained sealed systems achieve 80–90%. Verify with commissioning test.",
      action: "Commission performance test to confirm actual capture rate" });
  }
  if (!form.baseline_confirmation || form.baseline_confirmation === "assumed_default") {
    insights.push({ level: "warning", field: "baseline_confirmation",
      message: "Baseline is unconfirmed. VVB validation requires site survey or regulatory records. This prevents high data quality classification.",
      action: "Upload site inspection report and select 'Site Survey' confirmation" });
  }
  const capex = parseFloat(form.capex_usd || 0);
  const cashflow = parseFloat(form.electricity_tariff_usd_kwh || 0);
  if (capex > 0 && cashflow === 0) {
    insights.push({ level: "warning", field: "electricity_tariff_usd_kwh",
      message: "CAPEX entered but no electricity tariff set — DSCR and IRR cannot be computed. Financial model incomplete.",
      action: "Enter electricity tariff or tipping fee to enable DSCR calculation" });
  }
  return insights;
}

// ── Components ─────────────────────────────────────────────────────────────────

function TierSelector({ fieldKey, value, onChange }) {
  return (
    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
      <span className="text-xs text-slate-400 font-semibold mr-0.5">Tier:</span>
      {TIER_OPTIONS.map(t => (
        <button key={t.value} type="button" title={`${t.value} — ${t.full}: ${t.desc}`}
          onClick={() => onChange(`${fieldKey}_tier`, t.value)}
          className={`text-xs font-bold px-2 py-0.5 rounded-full border transition-all ${value === t.value ? TIER_ACTIVE[t.value] : t.color}`}
          data-testid={`tier-${fieldKey}-${t.value}`}>
          {t.label}
        </button>
      ))}
      {value && <span className="text-xs text-slate-400 italic">{TIER_OPTIONS.find(t => t.value === value)?.full}</span>}
    </div>
  );
}

function ValidationIndicator({ status, message }) {
  if (!status || status === "valid") return null;
  const cfg = {
    error:   { cls: "text-red-500",   bg: "bg-red-50 border-red-200",   icon: <XCircle size={11} className="text-red-500 flex-shrink-0 mt-0.5" /> },
    warning: { cls: "text-amber-600", bg: "bg-amber-50 border-amber-200", icon: <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" /> },
  }[status] || {};
  return (
    <div className={`flex items-start gap-1.5 mt-1.5 rounded-lg px-2 py-1 border ${cfg.bg}`}>
      {cfg.icon}
      <p className={`text-xs ${cfg.cls}`}>{message}</p>
    </div>
  );
}

function FieldMetaRow({ fieldKey, meta = {}, onChange, onAttachEvidence, evidenceCount = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1.5">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors">
        {open ? <ChevronUp size={11} /> : <ChevronRight size={11} />}
        <span>Source details</span>
        {(meta.source_reference || meta.comment) && <span className="text-teal-500">●</span>}
        {evidenceCount > 0 && <span className="bg-emerald-100 text-emerald-700 px-1.5 rounded-full text-xs">{evidenceCount} evidence</span>}
      </button>
      {open && (
        <div className="mt-2 space-y-2 pl-3 border-l-2 border-slate-100">
          <div>
            <label className="text-xs text-slate-500 font-semibold block mb-0.5">Source Type</label>
            <select value={meta.source_type || "default"}
              onChange={e => onChange(fieldKey, "source_type", e.target.value)}
              className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400 bg-white">
              {SOURCE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold block mb-0.5">Source Reference / Document</label>
            <input type="text" value={meta.source_reference || ""}
              onChange={e => onChange(fieldKey, "source_reference", e.target.value)}
              placeholder="e.g. 'BMP test report, March 2026, pg.4'"
              className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-semibold block mb-0.5">Comment / Assumption</label>
            <textarea value={meta.comment || ""}
              onChange={e => onChange(fieldKey, "comment", e.target.value)}
              rows={2} placeholder="Note any assumptions or data limitations..."
              className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400 resize-none" />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => onAttachEvidence(fieldKey)}
              className="flex items-center gap-1 text-xs px-2.5 py-1 border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-50 bg-white"
              data-testid={`attach-evidence-${fieldKey}`}>
              <Link2 size={10} /> Attach Evidence
            </button>
            {evidenceCount > 0 && <span className="text-xs text-emerald-600 font-semibold">{evidenceCount} attached</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function DataRolePills({ roles = [] }) {
  if (!roles || roles.length === 0) return null;
  return (
    <div className="flex gap-0.5 flex-wrap mt-0.5">
      {roles.slice(0, 4).map(r => {
        const cfg = ROLE_ICONS[r] || {};
        return (
          <span key={r} title={cfg.label || r}
            className="text-xs px-1 py-0 rounded bg-slate-100 text-slate-400 font-semibold">
            {cfg.label || r}
          </span>
        );
      })}
    </div>
  );
}

function DynamicField({ field, value, tierValue, meta, onChange, onMetaChange, onAttachEvidence, error, evidenceCount }) {
  const { key, label, unit, type, hint, range, options, ef_source, tier_selector, required, data_roles } = field;

  const inputCls = `w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:border-teal-400 bg-white text-slate-900 ${
    error ? "border-red-400 bg-red-50" : "border-slate-200"
  }`;

  const renderInput = () => {
    if (type === "select") return (
      <select value={value ?? ""} onChange={e => onChange(key, e.target.value)}
        className={inputCls} data-testid={`field-${key}`}>
        <option value="">— Select —</option>
        {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
    if (type === "text") return (
      <textarea value={value ?? ""} onChange={e => onChange(key, e.target.value)} rows={2}
        className={`${inputCls} resize-none`} placeholder={`${label}...`}
        data-testid={`field-${key}`} />
    );
    return (
      <input type="number" value={value ?? ""} onChange={e => onChange(key, e.target.value)}
        min={range?.min} max={range?.max} className={inputCls}
        placeholder={`Enter ${label.toLowerCase()}`} data-testid={`field-${key}`} />
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-1">
        <label className="text-xs font-semibold text-slate-600">
          {label}
          {unit && <span className="text-xs text-slate-400 font-normal ml-1">({unit})</span>}
          {ef_source && <span className="text-xs text-slate-400 font-normal ml-1">· {ef_source}</span>}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
        <DataRolePills roles={data_roles} />
      </div>
      {renderInput()}
      {hint && <p className="text-xs text-slate-400 mt-0.5 italic">{hint}</p>}
      {error && <p className="text-xs text-red-500 mt-0.5 font-semibold">{error}</p>}
      {tier_selector && (
        <TierSelector fieldKey={key} value={tierValue} onChange={onChange} />
      )}
      <FieldMetaRow
        fieldKey={key} meta={meta}
        onChange={onMetaChange}
        onAttachEvidence={onAttachEvidence}
        evidenceCount={evidenceCount || 0}
      />
    </div>
  );
}

function InsightCard({ insight, onDismiss }) {
  const cfg = {
    warning: { bg: "bg-amber-50 border-amber-200", icon: <AlertTriangle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" /> },
    info:    { bg: "bg-blue-50 border-blue-200",   icon: <Lightbulb size={13} className="text-blue-500 flex-shrink-0 mt-0.5" /> },
    error:   { bg: "bg-red-50 border-red-200",     icon: <XCircle size={13} className="text-red-500 flex-shrink-0 mt-0.5" /> },
  }[insight.level] || {};
  return (
    <div className={`border rounded-xl px-3 py-2.5 flex items-start gap-2 ${cfg.bg}`} data-testid="insight-card">
      {cfg.icon}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-800">{insight.message}</p>
        {insight.action && <p className="text-xs text-slate-500 italic mt-0.5">→ {insight.action}</p>}
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-slate-300 hover:text-slate-500 flex-shrink-0">
          <XCircle size={11} />
        </button>
      )}
    </div>
  );
}

function FieldGroup({ title, children, hasError, evidenceStatus }) {
  const [open, setOpen] = useState(true);
  const evBadge = evidenceStatus ? (() => {
    const { covered, total, status } = evidenceStatus;
    if (status === "complete") return { text: `${covered}/${total} ✓`, cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    if (status === "partial")  return { text: `${covered}/${total}`,   cls: "bg-amber-100 text-amber-700 border-amber-200" };
    return                            { text: `0/${total} ⚠`,         cls: "bg-red-50 text-red-600 border-red-200" };
  })() : null;
  return (
    <div className={`bg-white border rounded-xl overflow-hidden mb-4 ${hasError ? "border-red-300" : "border-slate-200"}`}>
      <button className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 text-left"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{title}</p>
          {hasError && <span className="text-xs text-red-500 font-bold">(validation required)</span>}
        </div>
        <div className="flex items-center gap-2">
          {evBadge && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${evBadge.cls}`}
              title="Evidence coverage for this section">
              {evBadge.text} evidence
            </span>
          )}
          {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
        </div>
      </button>
      {open && <div className="px-5 py-4 space-y-5">{children}</div>}
    </div>
  );
}

// ── Dynamic Feedstock Streams ──────────────────────────────────────────────────

// Internal IPCC proxy categories — values sent to engine, factor library UNCHANGED
// ── Feedstock methodology data ───────────────────────────────────────────────
const FEEDSTOCK_TYPES = [
  { value: "food_waste",        label: "Food waste (DOC 0.15 tC/t)", doc: 0.15 },
  { value: "mixed_msw",         label: "Mixed MSW (DOC 0.17 tC/t)",  doc: 0.17 },
  { value: "agricultural",      label: "Agricultural residues — CROP RESIDUES ONLY (DOC 0.14 tC/t)", doc: 0.14 },
  { value: "manure_management", label: "Manure Management / Anaerobic Digestion (VS-BMP, IPCC Vol.4 Ch.10)", doc: null },
  { value: "garden_waste",      label: "Garden / green waste (DOC 0.20 tC/t)", doc: 0.20 },
  { value: "sewage_sludge",     label: "Sewage sludge (DOC 0.05 tC/t)", doc: 0.05 },
];

// VS-BMP site-specific parameters (IPCC 2006 Vol.4 Ch.10 — Oko-Oba feasibility assessment data)
// Formula: CH4 (m³/yr) = Feedstock (t) × 1,000 × VS_fraction × BMP (m³CH4/kgVS)
const VS_BMP_DATA = {
  "Cattle dung":  { vs_fraction: 0.6418, vs_pct: 64.18, bmp: 0.499, animal: "Cattle" },
  "Sheep dung":   { vs_fraction: 0.2977, vs_pct: 29.77, bmp: 0.653, animal: "Sheep" },
  "Goat dung":    { vs_fraction: 0.5416, vs_pct: 54.16, bmp: 0.420, animal: "Goat" },
};

// Oko-Oba site streams — each stream carries full methodology metadata
const FEEDSTOCK_SITE_STREAMS = [
  {
    label: "Cattle dung", proxy: "manure_management", doc: null,
    dailyKg: 18750, note: "Dominant stream — ~80.7% of daily volume",
    methodology: "VS-BMP", vs_pct: 64.18, bmp: 0.499,
    ipcc_ref: "IPCC 2006 Vol.4 Ch.10 — Cattle manure (site-specific)",
    source: "Oko-Oba feasibility assessment (Green & Smart, 2025)",
  },
  {
    label: "Sheep dung", proxy: "manure_management", doc: null,
    dailyKg: 50, note: "Minor stream — ~0.2% of daily volume",
    methodology: "VS-BMP", vs_pct: 29.77, bmp: 0.653,
    ipcc_ref: "IPCC 2006 Vol.4 Ch.10 — Sheep manure (site-specific)",
    source: "Oko-Oba feasibility assessment (Green & Smart, 2025)",
  },
  {
    label: "Goat dung", proxy: "manure_management", doc: null,
    dailyKg: 178, note: "Minor stream — ~0.8% of daily volume",
    methodology: "VS-BMP", vs_pct: 54.16, bmp: 0.420,
    ipcc_ref: "IPCC 2006 Vol.4 Ch.10 — Goat manure (site-specific)",
    source: "Oko-Oba feasibility assessment (Green & Smart, 2025)",
  },
  {
    label: "Slaughterhouse blood", proxy: "food_waste", doc: 0.15,
    dailyKg: 750, note: "Estimated — pending site confirmation",
    methodology: "DOC-FOD", ipcc_ref: "IPCC 2006 Vol.5 Table 2.4",
    source: "Estimated — site records pending",
  },
  {
    label: "Sewage sludge", proxy: "sewage_sludge", doc: 0.05,
    dailyKg: 3500, note: "Estimated — pending site confirmation",
    methodology: "DOC-FOD", ipcc_ref: "IPCC 2006 Vol.5 Table 2.4",
    source: "Estimated — site records pending",
  },
  // Generic fallback options for other project types
  { label: "Food waste (generic)",            proxy: "food_waste",    doc: 0.15, dailyKg: null, note: "", methodology: "DOC-FOD" },
  { label: "Mixed MSW (generic)",             proxy: "mixed_msw",     doc: 0.17, dailyKg: null, note: "", methodology: "DOC-FOD" },
  { label: "Agricultural residues (generic)", proxy: "agricultural",  doc: 0.14, dailyKg: null, note: "Crop residues only — NOT for animal manure", methodology: "DOC-FOD" },
  { label: "Garden / green waste",            proxy: "garden_waste",  doc: 0.20, dailyKg: null, note: "", methodology: "DOC-FOD" },
  { label: "Sewage sludge (generic)",         proxy: "sewage_sludge", doc: 0.05, dailyKg: null, note: "", methodology: "DOC-FOD" },
];

// Helper: resolve display label from stream type + saved label
const resolveStreamLabel = (type, savedLabel) => {
  if (savedLabel) return savedLabel;
  const match = FEEDSTOCK_SITE_STREAMS.find(s => s.proxy === type);
  return match ? match.label : (FEEDSTOCK_TYPES.find(f => f.value === type)?.label || type);
};

function FeedstockStreamsManager({ streams, onUpdate }) {
  const total = streams.reduce((a, s) => a + parseFloat(s.pct || 0), 0);
  const isValid = Math.abs(total - 100) <= 0.5;

  // Weighted metric: for VS-BMP streams use BMP contribution; DOC-FOD uses DOC
  const hasManure = streams.some(s => s.type === "manure_management");

  const addStream = () => onUpdate([...streams, { id: Date.now(), type: "", label: "", pct: 0, source_tier: "T2", source_reference: "" }]);
  const removeStream = (id) => onUpdate(streams.filter(s => s.id !== id));
  const updateStream = (id, key, val) => onUpdate(streams.map(s => s.id === id ? { ...s, [key]: val } : s));

  const handleLabelSelect = (id, selectedLabel) => {
    const match = FEEDSTOCK_SITE_STREAMS.find(s => s.label === selectedLabel);
    if (match) {
      onUpdate(streams.map(s => s.id === id ? {
        ...s, label: match.label, type: match.proxy,
        vs_pct: match.vs_pct, bmp: match.bmp,
      } : s));
    } else {
      updateStream(id, "label", selectedLabel);
    }
  };

  return (
    <div>
      {/* Methodology disclosure banner */}
      <div className={`mb-3 px-3 py-2 rounded-lg text-xs border ${
        hasManure
          ? "bg-blue-50 border-blue-200 text-blue-800"
          : "bg-amber-50 border-amber-200 text-amber-800"
      }`}>
        {hasManure ? (
          <>
            <span className="font-bold">VS-BMP Manure Management methodology active.</span>{" "}
            Manure streams (cattle, sheep, goat) use site-specific VS fractions and BMP-derived methane yields
            (IPCC 2006 Vol.4 Ch.10). Non-manure streams use DOC-FOD (IPCC Vol.5 Table 2.4).{" "}
            <span className="font-semibold">No DOC values apply to manure streams.</span>
          </>
        ) : (
          <>
            <span className="font-bold">Multi-stream feedstock configuration.</span>{" "}
            Each stream uses its applicable IPCC methodology. Add manure streams to activate VS-BMP pathway.
          </>
        )}
      </div>

      <div className="space-y-3 mb-3">
        {streams.map((stream, idx) => {
          const siteEntry    = FEEDSTOCK_SITE_STREAMS.find(s => s.label === stream.label || s.proxy === stream.type);
          const isManure     = stream.type === "manure_management";
          const vsBmpData    = isManure ? (VS_BMP_DATA[stream.label] || siteEntry) : null;
          const proxyEntry   = FEEDSTOCK_TYPES.find(f => f.value === stream.type);

          return (
            <div key={stream.id} className={`border rounded-xl p-3 space-y-2 ${
              isManure ? "bg-blue-50/40 border-blue-200" : "bg-slate-50 border-slate-200"
            }`} data-testid={`feedstock-stream-row-${idx}`}>

              {/* Row 1: Label selector + share + tier */}
              <div className="flex items-start gap-2">
                <span className="text-xs font-bold text-slate-400 mt-2 flex-shrink-0">#{idx + 1}</span>
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-slate-500 font-semibold mb-0.5 block">Site Stream</label>
                    <select
                      value={stream.label || ""}
                      onChange={e => handleLabelSelect(stream.id, e.target.value)}
                      className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-lg bg-white"
                      data-testid={`stream-label-${idx}`}>
                      <option value="">— Select stream —</option>
                      <optgroup label="Oko-Oba Site Streams">
                        {FEEDSTOCK_SITE_STREAMS.slice(0, 5).map(f => (
                          <option key={f.label} value={f.label}>{f.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Generic Proxy Categories">
                        {FEEDSTOCK_SITE_STREAMS.slice(5).map(f => (
                          <option key={f.label} value={f.label}>{f.label}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-semibold mb-0.5 block">Share (%)</label>
                    <input type="number" value={stream.pct} min={0} max={100}
                      onChange={e => updateStream(stream.id, "pct", e.target.value)}
                      className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-lg"
                      data-testid={`stream-pct-${idx}`} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-semibold mb-0.5 block">Evidence Tier</label>
                    <select value={stream.source_tier || "T2"} onChange={e => updateStream(stream.id, "source_tier", e.target.value)}
                      className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-lg bg-white">
                      {TIER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.value} — {t.full}</option>)}
                    </select>
                  </div>
                </div>
                {streams.length > 1 && (
                  <button onClick={() => removeStream(stream.id)} className="text-red-400 hover:text-red-600 flex-shrink-0 mt-1">
                    <XCircle size={14} />
                  </button>
                )}
              </div>

              {/* Row 2: Methodology display */}
              {stream.type && (
                <div className="ml-6">
                  {isManure ? (
                    /* VS-BMP display for manure streams — NO DOC */
                    <div className={`flex flex-wrap gap-3 px-3 py-2 rounded-lg border ${
                      "bg-blue-50 border-blue-200"
                    }`} data-testid={`stream-methodology-${idx}`}>
                      <div className="text-[10px]">
                        <span className="text-blue-400">Calculation basis:</span>{" "}
                        <span className="font-bold text-blue-800">Manure Management / VS-BMP</span>
                        <span className="text-blue-400 ml-1">(IPCC 2006 Vol.4 Ch.10)</span>
                      </div>
                      {vsBmpData?.vs_pct != null && (
                        <div className="text-[10px]">
                          <span className="text-blue-400">VS fraction:</span>{" "}
                          <span className="font-bold text-emerald-700">{vsBmpData.vs_pct.toFixed(2)}%</span>
                        </div>
                      )}
                      {vsBmpData?.bmp != null && (
                        <div className="text-[10px]">
                          <span className="text-blue-400">Methane yield:</span>{" "}
                          <span className="font-bold text-emerald-700">{vsBmpData.bmp} m³ CH₄/kg VS</span>
                        </div>
                      )}
                      <div className="text-[10px] text-red-600 font-semibold ml-auto">
                        DOC factor: <span className="text-red-700">NOT APPLIED</span>
                      </div>
                      {siteEntry?.note && (
                        <div className="text-[10px] text-blue-600 italic w-full">{siteEntry.note}</div>
                      )}
                    </div>
                  ) : (
                    /* DOC display for non-manure streams */
                    <div className="flex items-center gap-3 px-3 py-1.5 bg-white border border-slate-200 rounded-lg"
                      data-testid={`stream-methodology-${idx}`}>
                      <div className="text-[10px] text-slate-500 font-medium">
                        <span className="text-slate-400">Calculation basis:</span>{" "}
                        <span className="font-bold text-slate-700">
                          {siteEntry?.methodology || "DOC-FOD"} — {proxyEntry?.label || stream.type}
                        </span>
                      </div>
                      <div className="text-[10px] text-emerald-700 font-medium ml-auto">
                        DOC = {proxyEntry?.doc?.toFixed(2) ?? "—"} tC/t
                        <span className="text-slate-400 font-normal ml-1">(IPCC 2006 Vol.5 Table 2.4)</span>
                      </div>
                      {siteEntry?.note && (
                        <div className="text-[10px] text-amber-700 italic ml-2">{siteEntry.note}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Row 3: Source reference */}
              <div className="ml-6">
                <input type="text" value={stream.source_reference || ""}
                  onChange={e => updateStream(stream.id, "source_reference", e.target.value)}
                  placeholder={siteEntry?.source || "Source reference (e.g. site survey, feasibility report)"}
                  className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-lg" />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        <button onClick={addStream} type="button"
          className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 px-3 py-1.5 border border-teal-200 rounded-lg hover:bg-teal-50">
          <PlusCircle size={12} /> Add Feedstock Stream
        </button>
        <div className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full ${
          isValid ? "bg-emerald-100 text-emerald-700" : "bg-red-50 text-red-600 border border-red-200"
        }`} data-testid="feedstock-streams-total">
          Total: {total.toFixed(2)}% {isValid ? "✓" : "(must equal 100.00%)"}
          {hasManure && isValid && (
            <span className="ml-2 font-normal text-blue-600">VS-BMP active</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PoA Configuration ─────────────────────────────────────────────────────────
function PoAConfigEditor({ value, onChange }) {
  const v = value || { is_poa: false, aggregation_intent: "", scaling_strategy: "", expected_projects: "", geographic_scope: "" };
  const update = (key, val) => onChange({ ...v, [key]: val });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <input type="checkbox" checked={!!v.is_poa} onChange={e => update("is_poa", e.target.checked)}
          className="rounded border-slate-300" id="poa_is_poa" />
        <label htmlFor="poa_is_poa" className="text-xs font-semibold text-slate-700">
          Register as Programme of Activities (PoA)
        </label>
      </div>
      {v.is_poa && (
        <div className="pl-4 border-l-2 border-teal-200 space-y-3">
          {[
            ["aggregation_intent",  "Aggregation Intent",   "e.g. Regional waste PoA — combine similar sites"],
            ["scaling_strategy",    "Scaling Strategy",     "e.g. Replicate model across 5 municipal sites"],
            ["expected_projects",   "Expected Projects",    "e.g. 8 CPAs within 5 years"],
            ["geographic_scope",    "Geographic Scope",     "e.g. Lagos, Ogun, Oyo states — Nigeria"],
          ].map(([k, lbl, ph]) => (
            <div key={k}>
              <label className="text-xs text-slate-500 font-semibold block mb-0.5">{lbl}</label>
              <input type="text" value={v[k] || ""} onChange={e => update(k, e.target.value)}
                placeholder={ph} className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Module Readiness Panel ────────────────────────────────────────────────────
function ModuleReadinessPanel({ calcData, form, fields, decisionData, projectState, journey }) {
  const tco2e    = calcData?.tco2e_yr || 0;
  const hasTco2e = tco2e > 0;
  const capex    = parseFloat(form?.capex_usd || 0);
  const tariff   = parseFloat(form?.electricity_tariff_usd_kwh || 0);
  const confScore = decisionData?.completeness_score || projectState?.completeness?.score || 0;
  const evPct    = (projectState?.completeness?.score || 0);
  const baseConf = form?.baseline_confirmation;
  const baseOk   = baseConf && baseConf !== "assumed_default";
  const requiredFields = fields.filter(f => f.required && !f.show_if);
  const filled = requiredFields.filter(f => form[f.key] && form[f.key] !== "").length;
  const fieldPct = requiredFields.length > 0 ? (filled / requiredFields.length) * 100 : 0;

  const modules = [
    {
      label: "Climate & Circularity",
      path:  "climate-snapshot",
      ready: hasTco2e,
      partial: fieldPct >= 50,
      reason: hasTco2e ? "Calculation complete" : fieldPct >= 50 ? "Run calculation to generate" : "Critical inputs missing",
    },
    {
      label: "SDG Contribution",
      path:  "sdg",
      ready: hasTco2e,
      partial: fieldPct >= 30,
      reason: hasTco2e ? "SDG metrics available" : "Requires emissions calculation",
    },
    {
      label: "Carbon Strategy",
      path:  "carbon-strategy",
      ready: hasTco2e && baseOk,
      partial: hasTco2e,
      reason: hasTco2e && baseOk ? "Pathways screened" : hasTco2e ? "Confirm baseline disposal" : "Requires calculation",
    },
    {
      label: "Carbon Structuring",
      path:  "carbon-structuring",
      ready: hasTco2e && confScore >= 50,
      partial: hasTco2e,
      reason: confScore >= 50 ? "Structuring workflow active" : hasTco2e ? "Improve data completeness" : "Requires calculation",
    },
    {
      label: "Financing",
      path:  "financing",
      ready: capex > 0 && tariff > 0 && hasTco2e,
      partial: capex > 0 || tariff > 0,
      reason: capex > 0 && tariff > 0 ? "DSCR model active" : "Enter CAPEX + tariff",
    },
    {
      label: "Reports",
      path:  "reports",
      ready: hasTco2e && confScore >= 60,
      partial: hasTco2e,
      reason: hasTco2e && confScore >= 60 ? "Reports available" : hasTco2e ? "Increase confidence score" : "Requires calculation",
    },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="module-readiness">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Module Readiness</p>
      <div className="space-y-1.5">
        {modules.map(m => {
          const status = m.ready ? "ready" : m.partial ? "partial" : "missing";
          const dot = { ready: "bg-emerald-500", partial: "bg-amber-400", missing: "bg-slate-300" }[status];
          const text = { ready: "text-emerald-700", partial: "text-amber-700", missing: "text-slate-400" }[status];
          const lbl  = { ready: "Ready", partial: "Partial", missing: "Missing" }[status];
          return (
            <div key={m.label} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
                <span className="text-slate-600">{m.label}</span>
              </div>
              <span className={`font-semibold ${text}`} title={m.reason}>{lbl}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Sticky Intelligence Panel ─────────────────────────────────────────────────
function StickyIntelligencePanel({ previewState, validationErrors, insights, form, fields, fieldMeta, projectState, dataQualityOutputs, journey, financialOutputs }) {
  const preview   = previewState;
  const decisionData = preview?.decision || {};
  const calcData  = preview?.calculation || {};
  const confScore = decisionData.completeness_score || 0;
  const confLevel = decisionData.completeness_level || "Early";
  const warnings  = decisionData.warnings_critical || [];
  const errorCount = Object.keys(validationErrors).length;
  const insightCount = insights.length;

  // Completeness from required fields
  const requiredFields = fields.filter(f => f.required && !f.show_if);
  const filled = requiredFields.filter(f => form[f.key] && form[f.key] !== "").length;
  const completenessLocal = requiredFields.length > 0 ? Math.round((filled / requiredFields.length) * 100) : 0;

  // Live confidence from tiers
  const tierFields = fields.filter(f => f.tier_selector);
  const tierScore = tierFields.length > 0 ? Math.round(
    tierFields.reduce((a, f) => {
      const tier = form[`${f.key}_tier`] || "T3";
      return a + (TIER_WEIGHT[tier] || 0.4);
    }, 0) / tierFields.length * 100
  ) : 0;

  const levelColor = {
    "Data Completeness: High": "text-emerald-700", "Advanced": "text-teal-700",
    "Developing": "text-amber-700", "Early": "text-red-600",
  };

  return (
    <div className="space-y-4">
      {/* Credibility Status — most prominent */}
      {projectState && (
        <CredibilityStatusBanner projectState={projectState} />
      )}

      {/* Confidence Breakdown */}
      <ConfidenceBreakdownPanel
        dataQualityOutputs={dataQualityOutputs}
        dataConfidence={journey?.data_confidence}
      />

      {/* Completeness + Tiers */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Dataset Completeness</p>
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${completenessLocal}%` }} />
          </div>
          <span className="text-xs font-bold text-slate-700">{completenessLocal}%</span>
        </div>
        <p className="text-xs text-slate-400">{filled}/{requiredFields.length} required fields</p>

        {confScore > 0 && (
          <>
            <div className="border-t border-slate-100 mt-3 pt-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Platform Confidence</p>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-bold ${levelColor[confLevel] || "text-slate-700"}`}>{confScore}/100</span>
                <span className={`text-xs font-semibold ${levelColor[confLevel] || "text-slate-500"}`}>{confLevel}</span>
              </div>
            </div>
          </>
        )}

        {/* Tier distribution */}
        {tierFields.length > 0 && (
          <div className="border-t border-slate-100 mt-3 pt-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Data Tiers</p>
            <div className="flex gap-1.5 flex-wrap">
              {["T1","T2","T3","T4"].map(tier => {
                const count = tierFields.filter(f => form[`${f.key}_tier`] === tier).length;
                if (count === 0) return null;
                const cls = { T1:"bg-emerald-100 text-emerald-700", T2:"bg-amber-100 text-amber-700", T3:"bg-slate-100 text-slate-600", T4:"bg-purple-100 text-purple-700" }[tier];
                return <span key={tier} className={`text-xs font-bold px-2 py-0.5 rounded-full ${cls}`}>{tier}: {count}</span>;
              })}
            </div>
          </div>
        )}
      </div>

      {/* Validation status */}
      {(errorCount > 0 || warnings.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-2">Validation Issues</p>
          {errorCount > 0 && (
            <p className="text-xs text-red-600 mb-1">{errorCount} blocking error(s) — must resolve before calculating</p>
          )}
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
              <AlertTriangle size={9} className="flex-shrink-0 mt-0.5" />
              {w.message?.slice(0, 80)}
            </p>
          ))}
        </div>
      )}

      {/* Derived variables */}
      {calcData.tco2e_yr > 0 && (
        <div className="bg-[#0F172A] rounded-xl p-4">
          <p className="text-xs font-bold text-slate-300 uppercase tracking-wide mb-3">Derived Variables (Live)</p>
          <div className="space-y-2">
            {[
              ["Annual Reduction", calcData.tco2e_yr > 0 ? `${Number(calcData.tco2e_yr).toFixed(1)} tCO₂e/yr` : "—", "text-teal-400"],
              ["Electricity Gen.", calcData.energy_mwh > 0 ? `${Number(calcData.energy_mwh).toLocaleString()} MWh/yr` : "—", "text-white"],
              ["Net Revenue",      calcData.revenue_usd_yr > 0 ? `$${Number(calcData.revenue_usd_yr).toLocaleString()}/yr` : "—", "text-emerald-400"],
              ["DSCR",             calcData.dscr != null ? `${calcData.dscr}x` : "—", calcData.dscr >= 1.2 ? "text-emerald-400" : "text-amber-400"],
            ].map(([l, v, c]) => (
              <div key={l} className="flex justify-between items-center">
                <span className="text-xs text-slate-400">{l}</span>
                <span className={`text-xs font-bold ${c}`}>{v}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-600 italic mt-2">Updates as you type</p>
        </div>
      )}

      {/* Insights */}
      {insightCount > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
            Smart Insights ({insightCount})
          </p>
          <div className="space-y-2">
            {insights.slice(0, 3).map((insight, i) => (
              <InsightCard key={i} insight={insight} />
            ))}
          </div>
        </div>
      )}

      {/* Financing signal */}
      {preview?.decision?.financing_signal && preview.decision.financing_signal !== "INSUFFICIENT_DATA" && (
        <div className={`rounded-xl p-4 border ${
          ["INVESTABLE"].includes(preview.decision.financing_signal) ? "bg-emerald-50 border-emerald-200" :
          preview.decision.financing_signal === "RESTRUCTURE_REQUIRED" ? "bg-amber-50 border-amber-200" :
          "bg-red-50 border-red-200"
        }`}>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Live Financing Signal</p>
          <p className="text-xs font-bold text-slate-800">
            {preview.decision.financing_signal === "INVESTABLE"
              ? "Financial Model: Structurally Viable"
              : preview.decision.financing_signal.replace(/_/g," ")}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">{preview.decision.financing_reason?.slice(0,80)}</p>
        </div>
      )}

      {/* Module readiness — which pages have enough data */}
      <ModuleReadinessPanel calcData={calcData} form={form} fields={fields} decisionData={decisionData} projectState={projectState} journey={journey} />
    </div>
  );
}

// ── Validation Engine ─────────────────────────────────────────────────────────
function runAuditValidation(form, fields, feedstockStreams) {
  const errors = {};
  const required = fields.filter(f => f.required && !f.show_if);
  required.forEach(f => {
    if (!form[f.key] && form[f.key] !== 0) errors[f.key] = `${f.label} is required`;
  });

  if (form.use_feedstock_split === "yes") {
    // When FeedstockStreamsManager is active, use its state as the authoritative source.
    // The legacy form.stream_1/2/3_pct only covers 3 streams and will incorrectly
    // flag a 5-stream config as invalid (e.g. 81.7% ≠ 100%).
    const activeStreams = feedstockStreams && feedstockStreams.length > 0
      ? feedstockStreams
      : null;

    if (activeStreams) {
      const total = activeStreams.reduce((a, s) => a + parseFloat(s.pct || 0), 0);
      if (Math.abs(total - 100) > 0.5) {
        errors["stream_split"] = `Stream percentages sum to ${total.toFixed(1)}% — must equal 100%`;
      }
      if (!activeStreams[0]?.type) errors["stream_1_type"] = "Stream 1 type required";
      if (!activeStreams[1]?.type) errors["stream_2_type"] = "Stream 2 type required";
    } else {
      // Fallback: legacy 3-stream validation
      const total = ["stream_1_pct","stream_2_pct","stream_3_pct"].reduce((a, k) => a + parseFloat(form[k] || 0), 0);
      if (Math.abs(total - 100) > 0.5) {
        errors["stream_split"] = `Stream percentages sum to ${total.toFixed(1)}% — must equal 100%`;
        errors["stream_1_pct"] = "Streams must sum to 100%";
        errors["stream_2_pct"] = "Streams must sum to 100%";
      }
      if (!form.stream_1_type) errors["stream_1_type"] = "Stream 1 waste type required";
      if (!form.stream_2_type) errors["stream_2_type"] = "Stream 2 waste type required";
    }
  }

  // Range checks
  [
    ["capture_efficiency_pct", 50, 98, "Capture efficiency"],
    ["ch4_fraction_pct", 40, 75, "Methane fraction"],
    ["electrical_efficiency_pct", 15, 45, "Electrical efficiency"],
    ["debt_ratio_pct", 0, 90, "Debt ratio"],
  ].forEach(([key, min, max, label]) => {
    const val = parseFloat(form[key]);
    if (form[key] && !isNaN(val) && (val < min || val > max)) {
      errors[key] = `${label}: ${val} is out of range (${min}–${max})`;
    }
  });

  // Dependency check
  if (["electricity","chp"].includes(form.energy_use) && !form.electrical_efficiency_pct) {
    errors["electrical_efficiency_pct"] = "Required when energy use includes electricity";
  }

  // CAPEX breakdown sum
  const capexKeys = ["capex_digester_pct","capex_gas_handling_pct","capex_power_gen_pct","capex_civil_pct","capex_grid_connection_pct","capex_contingency_pct"];
  const capexSum = capexKeys.reduce((a, k) => a + parseFloat(form[k] || 0), 0);
  if (capexSum > 0 && Math.abs(capexSum - 100) > 1) {
    errors["capex_digester_pct"] = `CAPEX breakdown sums to ${capexSum.toFixed(1)}% — must equal 100%`;
  }

  return errors;
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function GenericInputData() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    loading, journey, sectorCfg, sector, sectorLabel, methodology, org,
    calcVersion, refresh, inputSnapshot, draftInputSnapshot, evidenceCoverage,
    projectState, dataQualityOutputs, financialOutputs,
    calculationBasis, protocol, creditingPathway,
  } = useProjectWorkspace(id);

  // ── State ─────────────────────────────────────────────────────────────────
  const [form, setForm]               = useState({});
  const [fieldMeta, setFieldMeta]     = useState({});   // per-field metadata
  const [feedstockStreams, setFeedstockStreams] = useState([
    // Oko-Oba canonical 5-stream configuration — VS-BMP Manure Management methodology
    // Source: Green & Smart Feasibility Report 2025 + Oko-Oba technical team
    // Manure streams: IPCC 2006 Vol.4 Ch.10 (VS-BMP) — DOC NOT applied
    // Non-manure streams: IPCC 2006 Vol.5 Table 2.4 (DOC-FOD)
    { id:1, label:"Cattle dung",          type:"manure_management", pct:80.71, vs_pct:64.18, bmp:0.499, source_tier:"T2", source_reference:"Green & Smart Feasibility Report 2025" },
    { id:2, label:"Slaughterhouse blood", type:"food_waste",         pct:3.23,  source_tier:"T2", source_reference:"Estimated — pending site confirmation" },
    { id:3, label:"Sewage sludge",        type:"sewage_sludge",      pct:15.07, source_tier:"T2", source_reference:"Estimated — pending site confirmation" },
    { id:4, label:"Sheep dung",           type:"manure_management",  pct:0.22,  vs_pct:29.77, bmp:0.653, source_tier:"T2", source_reference:"Green & Smart Feasibility Report 2025" },
    { id:5, label:"Goat dung",            type:"manure_management",  pct:0.77,  vs_pct:54.16, bmp:0.420, source_tier:"T2", source_reference:"Green & Smart Feasibility Report 2025" },
  ]);
  const [poaConfig, setPoaConfig]     = useState({ is_poa: false });
  const [saving, setSaving]           = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showErrors, setShowErrors]   = useState(false);
  const [history, setHistory]         = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [mappedFields, setMappedFields] = useState(null);
  const [unmappedFields, setUnmappedFields] = useState([]);
  const [showMapping, setShowMapping] = useState(false);
  const [draftSaved, setDraftSaved]   = useState(false);
  const [livePreview, setLivePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dismissedInsights, setDismissedInsights] = useState(new Set());
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);
  const previewTimerRef = React.useRef(null);

  // ── Active insights ───────────────────────────────────────────────────────
  const allInsights = useMemo(() =>
    computeInsights(form, fieldMeta, sectorCfg?.input_fields || []),
    [form, fieldMeta, sectorCfg]
  );
  const insights = allInsights.filter(ins => !dismissedInsights.has(ins.field + ins.message));

  // ── Pre-fill from draft or last calculation ───────────────────────────────
  useEffect(() => {
    if (Object.keys(form).length > 0) return;
    const draftKeys = Object.keys(draftInputSnapshot || {}).length;
    const snapKeys  = Object.keys(inputSnapshot || {}).length;
    // Draft always takes priority — it represents the user's saved working state.
    // inputSnapshot is the last CALCULATED state; draft may differ from it intentionally.
    const source = draftKeys > 0 ? draftInputSnapshot : (snapKeys > 0 ? inputSnapshot : null);
    if (source && Object.keys(source).length > 0) {
      const prefilled = {};
      const metaPrefilled = {};
      Object.entries(source).forEach(([k, v]) => {
        if (k.endsWith("_source_type") || k.endsWith("_source_ref") || k.endsWith("_comment")) {
          const base = k.replace(/_source_type$|_source_ref$|_comment$/, "");
          const subKey = k.slice(base.length + 1);
          if (!metaPrefilled[base]) metaPrefilled[base] = {};
          metaPrefilled[base][subKey] = String(v);
        } else {
          prefilled[k] = String(v);
        }
      });
      setForm(prefilled);
      if (Object.keys(metaPrefilled).length > 0) setFieldMeta(metaPrefilled);
    }
    client.get(`/workspace/project/${id}/versions`).then(r => setHistory(r.data || [])).catch(() => {});
  }, [id, inputSnapshot, draftInputSnapshot]);

  // ── Sync feedstockStreams from pre-filled form (draft / snapshot restore) ──
  useEffect(() => {
    const s1type = form.stream_1_type;
    if (!s1type || form.use_feedstock_split !== "yes") return;
    const synced = [];
    for (let n = 1; n <= 5; n++) {
      const type = form[`stream_${n}_type`];
      const pct  = parseFloat(form[`stream_${n}_pct`] || 0);
      if (!type) break;
      const label    = form[`stream_${n}_label`] || resolveStreamLabel(type, null);
      const vsBmpInfo = (type === "manure_management") ? (VS_BMP_DATA[label] || null) : null;
      synced.push({
        id: n, type, label, pct,
        vs_pct:           vsBmpInfo?.vs_pct ?? null,
        bmp:              vsBmpInfo?.bmp    ?? null,
        source_tier:      form[`stream_${n}_tier`]       || "T2",
        source_reference: form[`stream_${n}_source_ref`] || "",
      });
    }
    if (synced.length > 0) {
      // Use functional update to avoid stale closure issues
      setFeedstockStreams(_prev => synced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.stream_1_type, form.use_feedstock_split]); // form values change triggers sync — intentional

  // ── Field change handler ──────────────────────────────────────────────────
  const handleChange = useCallback((key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (validationErrors[key]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
    setDraftSaved(false);
  }, [validationErrors]);

  // ── Field metadata change handler ────────────────────────────────────────
  const handleMetaChange = useCallback((fieldKey, metaKey, val) => {
    setFieldMeta(prev => ({
      ...prev,
      [fieldKey]: { ...(prev[fieldKey] || {}), [metaKey]: val },
    }));
    setDraftSaved(false);
  }, []);

  // ── Evidence attach handler ────────────────────────────────────────────────
  const handleAttachEvidence = useCallback((fieldKey) => {
    navigate(`/project/${id}/evidence-vault`);
    toast.info(`Navigate to Evidence Vault to attach evidence for: ${fieldKey}`);
  }, [id, navigate]);

  // ── Build inputs (includes metadata, backward-compatible) ─────────────────
  const buildInputs = useCallback(() => {
    const fields = sectorCfg?.input_fields || [];
    const inputs = {};
    fields.forEach(f => {
      const v = form[f.key];
      if (v === "" || v == null) {
        if (f.default != null) inputs[f.key] = f.default;
      } else if (f.type === "number" || f.type === "percent") {
        inputs[f.key] = parseFloat(v);
      } else {
        inputs[f.key] = v;
      }
      const tierKey = `${f.key}_tier`;
      if (form[tierKey]) inputs[tierKey] = form[tierKey];
      // Include field metadata (engine ignores these extra keys)
      const meta = fieldMeta[f.key];
      if (meta) {
        if (meta.source_type)      inputs[`${f.key}_source_type`]  = meta.source_type;
        if (meta.source_reference) inputs[`${f.key}_source_ref`]   = meta.source_reference;
        if (meta.comment)          inputs[`${f.key}_comment`]      = meta.comment;
      }
    });
    // Include PoA config
    if (poaConfig.is_poa) {
      Object.entries(poaConfig).forEach(([k, v]) => { inputs[`poa_${k}`] = v; });
    }
    // Include feedstock streams if using split
    // FeedstockStreamsManager is the SINGLE authoritative source for stream data.
    // Explicitly clear any legacy stream_N keys that the schema loop may have set
    // to guarantee no dual-source conflict.
    if (form.use_feedstock_split === "yes") {
      // Remove any legacy schema-written stream keys first
      for (let n = 1; n <= 5; n++) {
        delete inputs[`stream_${n}_type`];
        delete inputs[`stream_${n}_pct`];
        delete inputs[`stream_${n}_tier`];
        delete inputs[`stream_${n}_source_ref`];
      }
      // Write exclusively from feedstockStreams state — includes VS-BMP data for manure streams
      feedstockStreams.forEach((s, i) => {
        const n = i + 1;
        inputs[`stream_${n}_type`]        = s.type;
        inputs[`stream_${n}_label`]       = s.label || resolveStreamLabel(s.type, null);
        inputs[`stream_${n}_pct`]         = parseFloat(s.pct || 0);
        inputs[`stream_${n}_tier`]        = s.source_tier;
        inputs[`stream_${n}_source_ref`]  = s.source_reference;
      });

      // ── Methodology preservation guard ────────────────────────────────────
      // RULE: the stored methodology from the form/draft snapshot is authoritative.
      // buildInputs() must NEVER silently override an explicitly set methodology.
      // Only fall back to VS_BMP_MANURE when no methodology is explicitly selected.
      //
      // Without this guard: a project storing BIOGAS_YIELD would be overwritten to
      // VS_BMP_MANURE simply because it has manure streams, producing a 65,657 live
      // preview instead of the approved 4,883 tCO₂e/yr (Run 58 regression case).
      //
      // Approved methodology choices and their engine paths:
      //   BIOGAS_YIELD  → biogas yield × MCF correction (conservative; Oko-Oba Phase 1)
      //   VS_BMP_MANURE → VS fraction × BMP yield (requires lab-verified VS/BMP data)
      //   DOC_FOD       → IPCC FOD model for non-manure waste streams
      const hasManureStream   = feedstockStreams.some(s => s.type === "manure_management");
      const storedMethodology = form["feedstock_methodology"] || null;
      // Only infer VS_BMP_MANURE if no methodology is explicitly stored
      inputs["feedstock_methodology"] =
        storedMethodology || (hasManureStream ? "VS_BMP_MANURE" : "DOC_FOD");

      // Methodology audit note (for traceability)
      inputs["feedstock_proxy_mapping_note"] = hasManureStream
        ? "VS-BMP methodology active: manure streams use IPCC Vol.4 Ch.10 (VS fractions + BMP-derived methane yields). Non-manure streams use DOC-FOD (IPCC Vol.5 Table 2.4). DOC factors NOT applied to manure streams."
        : "DOC-FOD methodology: all streams use IPCC Vol.5 Table 2.4 weighted DOC.";
    }
    return inputs;
  }, [form, fieldMeta, sectorCfg, poaConfig, feedstockStreams]);

  // ── Debounced live preview (preview-state endpoint, no DB write) ──────────
  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    const fields = sectorCfg?.input_fields || [];
    const required = fields.filter(f => f.required && !f.show_if);
    const isReady  = required.every(f => form[f.key] && form[f.key] !== "" && form[f.key] !== "0");
    if (!isReady || Object.keys(form).length === 0) { setLivePreview(null); return; }
    previewTimerRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const inputs = buildInputs();
        const res = await client.post(`/workspace/project/${id}/preview-state`, { inputs });
        if (res.data.valid) setLivePreview(res.data);
        else setLivePreview(null);
      } catch { setLivePreview(null); }
      setPreviewLoading(false);
    }, 800);
    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [form, id, sectorCfg, buildInputs]);

  // ── Save Draft ────────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const inputs = buildInputs();
      await client.post(`/workspace/project/${id}/save-draft`, { inputs });
      setDraftSaved(true);
      toast.success("Draft saved — inputs preserved.");
    } catch { toast.error("Failed to save draft."); }
    setSaving(false);
  };

  // ── Run Calculation ───────────────────────────────────────────────────────
  const handleCalculate = async () => {
    const fields = sectorCfg?.input_fields || [];
    const errors = runAuditValidation(form, fields, feedstockStreams);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowErrors(true);
      toast.error(`${Object.keys(errors).length} validation issue(s) — review highlighted fields.`);
      return;
    }
    setValidationErrors({}); setShowErrors(false);
    setCalculating(true);
    try {
      const inputs = buildInputs();
      const res = await client.post(`/workspace/project/${id}/calculate`, {
        inputs,
        version_label: `v${(calcVersion?.number || 0) + 1} — ${new Date().toLocaleDateString()}`,
      });
      invalidateProjectWorkspaceCache(id);
      refresh();
      if (res.data.duplicate) toast.info("Inputs unchanged — existing calculation still current.");
      else {
        toast.success(`v${res.data.version_number} calculated · ${res.data.calculation_hash?.slice(0,10)}...`);
        client.get(`/workspace/project/${id}/versions`).then(r => setHistory(r.data || [])).catch(() => {});
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 422 && e.response?.data?.detail?.field) {
        const d = e.response.data.detail;
        setValidationErrors({ [d.field]: d.message });
        setShowErrors(true);
        toast.error(`Validation: ${d.message}`);
      } else {
        toast.error(`Calculation failed: ${detail}`);
      }
    }
    setCalculating(false);
  };

  // ── File upload (CSV/Excel) ────────────────────────────────────────────────
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    if (!["csv","json","xlsx","xls"].includes(file.name.split(".").pop().toLowerCase())) {
      toast.error("Supported: CSV, Excel (.xlsx/.xls), JSON"); setUploading(false); return;
    }
    try {
      const token = localStorage.getItem("decarboniq_token");
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/workspace/project/${id}/parse-inputs`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd }
      );
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Parse failed"); }
      const data = await res.json();
      setMappedFields(data.mapped || {}); setUnmappedFields(data.unmapped || []); setShowMapping(true);
      toast.success(`Parsed ${data.total_mapped} of ${data.total_parsed} fields.`);
    } catch (e) { toast.error(e.message || "Failed to parse file."); }
    setUploading(false);
  };

  const applyMappedValues = () => {
    if (!mappedFields) return;
    setForm(prev => { const u = { ...prev }; Object.entries(mappedFields).forEach(([k, v]) => { u[k] = String(v); }); return u; });
    setShowMapping(false); setMappedFields(null);
    toast.success(`${Object.keys(mappedFields).length} fields applied.`);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const fields = sectorCfg?.input_fields || [];
  const groups = {};
  fields.forEach(f => {
    if (f.show_if) {
      const condKey = Object.keys(f.show_if)[0];
      const condVals = f.show_if[condKey];
      const currentVal = form[condKey] ?? sectorCfg?.input_fields?.find(fld => fld.key === condKey)?.default;
      const normalizedCurrent = currentVal === undefined || currentVal === null ? "" : String(currentVal);
      const matches = condVals.some(v =>
        (v === null || v === "" || v === undefined)
          ? (normalizedCurrent === "" || normalizedCurrent === "null")
          : String(v) === normalizedCurrent
      );
      if (!matches) return;
    }
    const g = f.group || "General";
    if (!groups[g]) groups[g] = [];
    groups[g].push(f);
  });

  const errorCount = Object.keys(validationErrors).length;
  const groupsWithErrors = new Set(fields.filter(f => validationErrors[f.key]).map(f => f.group || "General"));
  const isUsingSplit = form.use_feedstock_split === "yes";

  // Evidence status per group
  const groupEvidenceStatus = {};
  if (sectorCfg?.evidence_requirements) {
    Object.entries(sectorCfg.evidence_requirements).forEach(([param, req]) => {
      const section = req.section || "General";
      if (!groupEvidenceStatus[section]) groupEvidenceStatus[section] = { total: 0, covered: 0, critical_missing: 0 };
      groupEvidenceStatus[section].total++;
      if (evidenceCoverage?.has(param)) groupEvidenceStatus[section].covered++;
      else if (req.critical) groupEvidenceStatus[section].critical_missing++;
    });
    Object.values(groupEvidenceStatus).forEach(s => {
      s.status = s.covered === s.total ? "complete" : s.covered > 0 ? "partial" : "missing";
    });
  }

  // Evidence count per field (from evidence items)
  const evidenceCountByField = {};
  (evidenceCoverage?.items || []).forEach(e => {
    if (e.linked_parameter) {
      evidenceCountByField[e.linked_parameter] = (evidenceCountByField[e.linked_parameter] || 0) + 1;
    }
  });

  return (
    <>
    <div className="p-6 lg:p-8 min-h-screen">
      {/* Top navigation */}
      <button onClick={() => navigate(`/project/${id}/workspace`)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
        <ArrowLeft size={13} /> {sectorLabel}
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">{org.name}</p>
          <PageHeader title={journey?.name || sectorLabel} />
          <p className="text-xs text-slate-400 mt-0.5">Input Data · {protocol || methodology}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.json,.xlsx,.xls" className="hidden"
              onChange={e => handleFileUpload(e.target.files?.[0])} />
            <div className={`flex items-center gap-1.5 px-4 py-2.5 border border-teal-300 text-teal-700 text-sm font-bold rounded-xl hover:bg-teal-50 ${uploading ? "opacity-50" : ""}`}>
              {uploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload CSV
            </div>
          </label>
          <button onClick={handleSaveDraft} disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-50 disabled:opacity-50"
            data-testid="save-draft-btn">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <FileText size={14} />}
            {draftSaved ? "Draft Saved ✓" : "Save Draft"}
          </button>
          <button onClick={handleCalculate} disabled={calculating}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50"
            data-testid="run-calculation-btn">
            {calculating ? <><RefreshCw size={14} className="animate-spin" /> Calculating...</> : <><FlaskConical size={14} /> Run Calculation</>}
          </button>
        </div>
      </div>

      {/* Validation error banner */}
      {showErrors && errorCount > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl px-5 py-3 mb-5 flex items-start gap-3" data-testid="validation-error-banner">
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-700">{errorCount} validation issue{errorCount > 1 ? "s" : ""} — resolve before calculating</p>
            <ul className="text-xs text-red-600 mt-1 list-disc list-inside space-y-0.5">
              {Object.entries(validationErrors).slice(0, 5).map(([k, v]) => <li key={k}>{v}</li>)}
            </ul>
          </div>
          <button onClick={() => setShowErrors(false)} className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0">Dismiss</button>
        </div>
      )}

      {/* Upload mapping preview */}
      {showMapping && mappedFields && (
        <div className="bg-white border-2 border-teal-300 rounded-xl p-5 mb-5" data-testid="upload-mapping-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-500" />
              <p className="text-sm font-bold text-slate-900">Upload Mapping — Review Before Applying</p>
            </div>
            <button onClick={() => { setShowMapping(false); setMappedFields(null); }} className="text-xs text-slate-400">Dismiss</button>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {Object.entries(mappedFields).map(([k, v]) => (
              <div key={k} className="bg-teal-50 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">{k.replace(/_/g," ")}</span>
                <span className="text-xs font-bold text-teal-800">{String(v).slice(0,20)}</span>
              </div>
            ))}
          </div>
          {unmappedFields.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-amber-600 font-semibold mb-1 flex items-center gap-1">
                <AlertTriangle size={10} /> {unmappedFields.length} fields not matched
              </p>
              <div className="flex flex-wrap gap-1.5">
                {unmappedFields.map(f => (
                  <span key={f.header} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                    {f.header}: {String(f.value).slice(0,15)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <button onClick={applyMappedValues}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700"
            data-testid="apply-mapped-btn">
            <CheckCircle2 size={12} /> Apply {Object.keys(mappedFields).length} Fields
          </button>
        </div>
      )}

      {/* Schema context strip */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Info size={12} className="text-teal-500" />
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Schema Context</p>
        </div>
        {[
          ["Sector",            sectorLabel || sector],
          ["Calculation Basis", calculationBasis || "IPCC 2006 Guidelines"],
          ["Protocol",          protocol || sectorCfg?.protocol || methodology || "—"],
          ["Crediting Pathway", creditingPathway !== null && creditingPathway !== undefined ? creditingPathway : "Not Assigned"],
          ["Boundary",          sectorCfg?.system_boundary?.split(".")[0] || "—"],
          ["Country",           org.country || "—"],
        ].map(([l, v]) => (
          <div key={l} className="flex items-center gap-1">
            <span className="text-xs text-slate-400">{l}:</span>
            <span className={`text-xs font-semibold ${l === "Crediting Pathway" && v === "Not Assigned" ? "text-amber-600" : "text-slate-600"}`}>{v}</span>
          </div>
        ))}
        {form.baseline_confirmation && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
            form.baseline_confirmation === "assumed_default"
              ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-emerald-50 border-emerald-200 text-emerald-700"
          }`} data-testid="baseline-audit-status">
            Baseline: {form.baseline_confirmation === "assumed_default" ? "Assumed" : "Confirmed"}
          </span>
        )}
      </div>

      {/* Methodology disclosure — static, must always be visible */}
      <MethodologyPanel
        methodology={methodology}
        calculationBasis={calculationBasis}
        protocol={protocol}
        creditingPathway={creditingPathway}
        sector={sector}
        compact={true}
      />

      {/* Version / draft status */}
      {calcVersion ? (
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5 mb-5 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold text-teal-800">{calcVersion.label}</p>
              {/* Lock status badge */}
              {calcVersion.is_report_baseline && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 border border-emerald-300 text-emerald-700">
                  <Lock size={8} /> Report Baseline
                </span>
              )}
              {calcVersion.lock_status === "approved" && !calcVersion.is_report_baseline && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 border border-emerald-300 text-emerald-700">
                  <Shield size={8} /> Approved
                </span>
              )}
              {calcVersion.is_locked && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-slate-100 border border-slate-300 text-slate-500">
                  <Lock size={8} /> Locked
                </span>
              )}
            </div>
            <p className="text-xs text-teal-600">Hash: {calcVersion.hash?.slice(0,16)}... · {calcVersion.updated_by}</p>
          </div>
          <div className="flex items-center gap-2">
            {draftSaved && <span className="text-xs text-amber-600 font-bold bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Draft saved</span>}
            <span className="text-xs text-teal-700 font-bold bg-teal-100 px-2 py-0.5 rounded-full">v{calcVersion.number}</span>
            <button
              data-testid="version-history-btn"
              onClick={() => setVersionDrawerOpen(true)}
              className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold rounded-lg transition-colors">
              <GitBranch size={11} />
              History
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-5 flex items-center gap-2">
          <Info size={13} className="text-amber-500" />
          <p className="text-xs text-amber-700">No calculation yet. Fill required fields (<span className="text-red-400">*</span>) and click <strong>Run Calculation</strong>.</p>
        </div>
      )}

      {/* 2-column layout: form (left) + intelligence panel (right) */}
      <div className="flex gap-5 items-start">

        {/* ── MAIN FORM ──────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">

          {/* Preview state placeholder / loading */}
          {previewLoading && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-2">
              <RefreshCw size={13} className="text-teal-500 animate-spin flex-shrink-0" />
              <span className="text-xs text-slate-500">Computing live preview...</span>
            </div>
          )}

          {/* Dynamic field groups */}
          {Object.entries(groups).map(([groupName, groupFields]) => {
            const isFeedstock = groupName === "Feedstock";
            // When FeedstockStreamsManager is active, suppress individual schema stream fields
            // (stream_N_type, stream_N_pct, stream_N_share) to avoid duplicate UI.
            // The manager handles all stream data collection; schema fields are for generic projects.
            const SCHEMA_STREAM_KEYS = /^stream_[1-5]_(type|pct|share|tier|source_ref)$/;
            return (
              <FieldGroup key={groupName} title={groupName}
                hasError={groupsWithErrors.has(groupName)}
                evidenceStatus={groupEvidenceStatus[groupName]}>
                {groupFields.map(f => {
                  // Skip schema stream fields when FeedstockStreamsManager is active
                  if (isFeedstock && isUsingSplit && SCHEMA_STREAM_KEYS.test(f.key)) return null;
                  return (
                    <DynamicField
                      key={f.key}
                      field={f}
                      value={form[f.key] ?? (f.default != null ? String(f.default) : "")}
                      tierValue={form[`${f.key}_tier`]}
                      meta={fieldMeta[f.key]}
                      onChange={handleChange}
                      onMetaChange={handleMetaChange}
                      onAttachEvidence={handleAttachEvidence}
                      error={validationErrors[f.key]}
                      evidenceCount={evidenceCountByField[f.key] || 0}
                    />
                  );
                })}

                {/* Feedstock split: show dynamic stream manager when enabled */}
                {isFeedstock && isUsingSplit && (
                  <div>
                    <p className="text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">Multi-Stream Feedstock Configuration</p>
                    <FeedstockStreamsManager
                      streams={feedstockStreams}
                      onUpdate={setFeedstockStreams}
                    />
                  </div>
                )}

                {/* Feedstock split sum error */}
                {isFeedstock && validationErrors["stream_split"] && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
                    <AlertTriangle size={12} className="text-red-500" />
                    <p className="text-xs text-red-600 font-semibold">{validationErrors["stream_split"]}</p>
                  </div>
                )}

                {/* Evidence vault shortcut for groups with missing evidence */}
                {groupEvidenceStatus[groupName]?.status === "missing" && groupEvidenceStatus[groupName]?.total > 0 && (
                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={12} className="text-amber-500" />
                      <p className="text-xs text-amber-700 font-semibold">
                        {groupEvidenceStatus[groupName].critical_missing > 0
                          ? `${groupEvidenceStatus[groupName].critical_missing} critical evidence document(s) missing`
                          : "Evidence documents not yet uploaded for this section"}
                      </p>
                    </div>
                    <button onClick={() => navigate(`/project/${id}/evidence-vault`)}
                      className="text-xs text-teal-600 font-bold hover:underline flex-shrink-0">
                      Upload Evidence →
                    </button>
                  </div>
                )}

                {/* PoA section: replace dropdown with structured config */}
                {groupName === "Programme of Activities" && (
                  <div>
                    <p className="text-xs font-bold text-slate-600 mb-2 uppercase tracking-wide">PoA Configuration</p>
                    <PoAConfigEditor value={poaConfig} onChange={setPoaConfig} />
                  </div>
                )}
              </FieldGroup>
            );
          })}

          {/* Version history — compact button → opens drawer */}
          {history.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Version History</p>
                <button
                  data-testid="open-version-drawer-btn"
                  onClick={() => setVersionDrawerOpen(true)}
                  className="flex items-center gap-1 text-xs text-teal-600 font-bold hover:text-teal-500 transition-colors">
                  <GitBranch size={11} />
                  View All {history.length} Versions →
                </button>
              </div>
              {/* Show just the latest 2 runs as a compact summary */}
              <div className="space-y-1.5">
                {history.slice(0, 2).map(v => {
                  const isBaseline = v.is_report_baseline;
                  const isSuperseded = v.lock_status === "superseded";
                  return (
                    <div key={v.version_number} className={`flex items-center justify-between text-xs py-1.5 px-2 rounded-lg
                      ${isBaseline ? "bg-emerald-50 border border-emerald-200" : isSuperseded ? "bg-amber-50/50 border border-amber-100" : "bg-slate-50 border border-slate-100"}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {isBaseline && <Shield size={9} className="text-emerald-600 flex-shrink-0" />}
                        {isSuperseded && <AlertTriangle size={9} className="text-amber-500 flex-shrink-0" />}
                        <span className={`font-semibold truncate ${isBaseline ? "text-emerald-800" : isSuperseded ? "text-amber-700" : "text-slate-700"}`}>
                          {v.version_label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {v.output_summary?.total_tco2e_yr > 0 && (
                          <span className={`font-bold ${isBaseline ? "text-emerald-700" : isSuperseded ? "text-amber-600" : "text-slate-600"}`}>
                            {Number(v.output_summary.total_tco2e_yr).toLocaleString("en-US", { maximumFractionDigits: 0 })} tCO₂e/yr
                          </span>
                        )}
                        {isBaseline && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Baseline</span>}
                        {isSuperseded && <span className="text-[9px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">Superseded</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── STICKY RIGHT PANEL ──────────────────────────────────────────── */}
        <div className="w-72 flex-shrink-0" data-testid="intelligence-panel">
          <div className="sticky top-6">
            <StickyIntelligencePanel
              previewState={livePreview}
              validationErrors={validationErrors}
              insights={insights}
              form={form}
              fields={fields}
              fieldMeta={fieldMeta}
              projectState={projectState}
              dataQualityOutputs={dataQualityOutputs}
              journey={journey}
              financialOutputs={financialOutputs}
            />
          </div>
        </div>

      </div>
    </div>

    {/* ── Version History Drawer ──────────────────────────────────────────── */}
    <VersionHistoryDrawer
      projectId={id}
      isOpen={versionDrawerOpen}
      onClose={() => setVersionDrawerOpen(false)}
    />
    </>
  );
}
